import type { CopyableTeamMap, GeoLine } from '@wehunt/ui'
import { listGroupHuntingGrounds } from './api/huntingGroundsApi'
import { listTeamMapPins } from './api/teamMapPinsApi'
import { listTeamMapLines } from './api/teamMapLinesApi'
import { dtoToGeoArea, dtoToMapObject } from './mapDtoMappers'
import { dtoToGeoLine } from './mapLineDtoMappers'
import { resolveImageUrls } from './useGroupMapPins'

/**
 * CLUB-188: fetch the copyable content of a source team's hunting-ground map —
 * its boundary, sub-areas, forbidden areas, pins and lines. Areas, pins and
 * lines load in parallel; the result is mapped to the shared
 * {@link CopyableTeamMap} shape the map editor's copy flow consumes.
 *
 * CLUB-528: `lines` here includes trails — this loader fetches everything the
 * source team has, unfiltered. It is the copy flow (`appendCopiedMap`) that
 * excludes trails when it merges the copy into the target map, so the
 * trails-are-not-copyable decision lives in exactly one place.
 */
export async function loadCopyableTeamMap(teamId: string, token: string): Promise<CopyableTeamMap> {
  const [grounds, pins, lineDtos] = await Promise.all([
    listGroupHuntingGrounds(teamId, token),
    listTeamMapPins(teamId, token),
    listTeamMapLines(teamId, token),
  ])

  const areas = grounds.areas
  // Resolved so the copied pins show their pictures in the editor before the
  // save duplicates them.
  const objects = await Promise.all(pins.map((p) => resolveImageUrls(dtoToMapObject(p))))
  return {
    boundaryAreas:   areas.filter((a) => a.areaType === 'hunt').map(dtoToGeoArea),
    subAreaPolygons: areas.filter((a) => a.areaType === 'sub').map(dtoToGeoArea),
    forbiddenPolygons: areas.filter((a) => a.areaType === 'forbidden').map(dtoToGeoArea),
    objects,
    // Skip a malformed document rather than throwing, mirroring the
    // element-wise skip used elsewhere for this collection (useGroupMapLines).
    lines: lineDtos.map(dtoToGeoLine).filter((l): l is GeoLine => l !== null),
  }
}
